import React from 'react';
import { Space, Tag, Tooltip } from 'antd';
import SummaryCard from './SummaryCard';
import { FormatDate } from '@/components/PrettyDate';
import { F } from '@/utils/intl';
import { SalesInfluencerModel } from '@/typings/models/SalesInfluencer';
import { TrickparkSession } from '@/typings/models/Trickpark';
import {
  formatTrickparkDuration,
  formatTrickparkTime,
} from '@/utils/trickpark';

// Name the backend assigns to the auto-created Trickpark influencer
// (TrickparkService::SALES_INFLUENCER_NAME).
const TRICKPARK_INFLUENCER_NAME = 'Trickpark';

export interface SalesInfluencersCardProps {
  salesInfluencers: SalesInfluencerModel[];
  trickparkSessions?: TrickparkSession[];
}

const renderMetaTooltip = (meta: SalesInfluencerModel['meta']) => {
  if (!meta || Object.keys(meta).length === 0) return null;
  return (
    <Space direction="vertical" size={0}>
      {Object.entries(meta).map(([key, value]) => (
        <span key={key}>
          {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
        </span>
      ))}
    </Space>
  );
};

const renderTrickparkSessionsTooltip = (sessions: TrickparkSession[]) => (
  <Space direction="vertical" size={8}>
    {sessions.map((session) => (
      <Space key={session.id} direction="vertical" size={0}>
        {session.session_type && <span>{session.session_type}</span>}
        <span>
          <FormatDate date={session.session_date} onlyDate />
          {': '}
          {formatTrickparkTime(session.session_start)} -{' '}
          {formatTrickparkTime(session.session_end)}
        </span>
        <span>{formatTrickparkDuration(session.duration_seconds)}</span>
      </Space>
    ))}
  </Space>
);

/**
 * "Sales Influencers" section in the lead details (CLICKUP-86catytnx).
 * Read-only tags — assignment is programmatic (API only). Meta data (already
 * merged entity + per-assignment values by the backend) shows on hover; the
 * Trickpark tag shows the lead's Trickpark sessions instead.
 */
const SalesInfluencersCard: React.FC<SalesInfluencersCardProps> = ({
  salesInfluencers,
  trickparkSessions,
}) => {
  const sortedTrickparkSessions = React.useMemo(
    () =>
      [...(trickparkSessions || [])].sort((a, b) =>
        `${a.session_date} ${a.session_start}`.localeCompare(
          `${b.session_date} ${b.session_start}`,
        ),
      ),
    [trickparkSessions],
  );

  return (
    <SummaryCard
      title={<F id={'entity.sales_influencer.plural'} defaultMessage={'Sales Influencers'} />}
      count={salesInfluencers.length}
    >
      <Space size={[4, 8]} wrap>
        {salesInfluencers.map((salesInfluencer) => {
          const isTrickpark =
            salesInfluencer.name === TRICKPARK_INFLUENCER_NAME &&
            sortedTrickparkSessions.length > 0;
          const tooltip = isTrickpark
            ? renderTrickparkSessionsTooltip(sortedTrickparkSessions)
            : renderMetaTooltip(salesInfluencer.meta);
          const tag = <Tag key={salesInfluencer.id}>{salesInfluencer.name}</Tag>;
          return tooltip ? (
            <Tooltip key={salesInfluencer.id} title={tooltip}>
              {tag}
            </Tooltip>
          ) : (
            tag
          );
        })}
      </Space>
    </SummaryCard>
  );
};

export default SalesInfluencersCard;
